/**
 * Encode/decode for the `?f=` shared-filter-link query parameter
 * (specs/shareable-context-links.md § Encoding). base64url of the JSON
 * `SharedFilterLinkEnvelope` — a `RuntimeFilterEnvelope` plus the
 * `contextSystemName` it is addressed to (D-3,
 * specs/visualization-agnostic-context-shell.md) — no compression, no
 * short-link record; see the spec's "Rejected" subsections for why.
 *
 * `decodeFilterLink` NEVER returns the raw `JSON.parse` output. It rebuilds a
 * fresh envelope containing only `contextSystemName`/`global`/`byLevelId`
 * and, within the latter two, only the five canonical `UnifiedFilters` lanes
 * — every key is copied by its literal, known-safe name (never a dynamic
 * bracket assignment from an attacker-controlled key) except `byLevelId`'s
 * per-level keys, which ARE attacker-controlled and are explicitly checked
 * against `UNSAFE_KEYS` before assignment. This is the first
 * `composeLevelFilters` input sourced from a URL rather than from stored
 * config, and `deepMergeJson.ts`'s `isPlainObject` guard excludes nothing
 * prototype-related — so a `"__proto__"`/`"constructor"`/`"prototype"` level
 * id in a crafted `?f=` must never reach a bracket-assignment target.
 */
import type { RuntimeFilterEnvelope, SharedFilterLinkEnvelope } from 'types/context';
import type { ArcFilter, FilterExpression, FilterExpressionType, FilterGroup, FilterOperator, PerspectiveFilter, RelationshipFilter, UnifiedFilters } from 'types/filters';
import { isFilterVariable, isPerspectiveLeaf, isPropertyLeaf, isRelationshipLeaf } from '@center-platform/spheres/filters';

/** Product constant (specs/shareable-context-links.md § Encoding) — below the ~2000-char figure
 *  safe across browsers/proxies/server defaults, leaving headroom for the rest of the URL. */
export const SHARE_LINK_MAX_CHARS = 1500;

const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

/**
 * Stack-safety bound on `groups` nesting, NOT a product limit on how deep a
 * filter may be authored (`FilterGroupEditor` supports arbitrary depth).
 *
 * `isValidFilterGroup` recurses over attacker-controlled structure, so its
 * depth has to be bounded by something. Today `SHARE_LINK_MAX_CHARS` bounds it
 * incidentally — 1500 base64url chars decode to ~1125 bytes and a nesting level
 * costs ~30 of them, so ~37 is the deepest a real link can express, and this
 * cap can never reject one. But that budget is a PRODUCT number the spec
 * explicitly leaves open ("is 1500 the right budget?"), and stack safety must
 * not silently depend on it: raise the budget and an unbounded validator starts
 * throwing `RangeError` again on a crafted link, from inside the mount effect
 * that has no `try`. Bounding here decouples the two.
 */
const MAX_FILTER_GROUP_DEPTH = 64;

/**
 * The canonical `FilterOperator` set (/standards/shared/filtering.md Rule 2), as a
 * runtime guard. Declared as `Record<FilterOperator, true>` rather than an array so
 * that adding a member to the union without adding it here is a COMPILE error — a
 * hand-maintained array silently drifts, which is exactly how
 * `filterEvaluation.ts`'s `VALID_OPERATORS` came to be missing `not_exists` and
 * `date_within_days`. That helper is deliberately not reused here for that reason;
 * borrowing it would silently drop two legitimate operators from shared links.
 */
const CANONICAL_OPERATORS: Record<FilterOperator, true> = {
  equals: true, not_equals: true,
  greater_than: true, less_than: true, greater_than_equals: true, less_than_equals: true,
  contains: true, in: true, not_in: true, exists: true, not_exists: true,
  starts_with: true, ends_with: true,
  is_true: true, is_false: true,
  is_empty: true, is_not_empty: true,
  between: true,
  date_within_days: true,
  last_7_days: true, last_30_days: true,
  within_last_days: true, within_next_days: true,
  not_contains: true, contains_all: true,
};

/** Same compile-enforced-union technique as `CANONICAL_OPERATORS`, for the same reason:
 *  `type` reaches the execute request, and a non-`property` type is what lets an
 *  expression legitimately carry an empty `property`. */
const CANONICAL_EXPRESSION_TYPES: Record<FilterExpressionType, true> = {
  property: true,
  children_count: true,
  approaches_deployed: true,
  elapsed_in_stage: true,
  arc_status: true,
  custom: true,
};

/**
 * A `?f=` payload is the first `UnifiedFilters` input sourced from a URL rather
 * than from stored config, so lane ARRAYS cannot be trusted element-wise either —
 * the `UNSAFE_KEYS` guard only covers `byLevelId`'s keys. A property clause whose
 * operator is outside the canonical set is never coerced — coercing would invent a
 * filter the sender never wrote, and a crafted operator flows straight into the
 * execute request and the chip display. It is not dropped either: returning `false`
 * here rejects the whole link (see `sanitizeUnifiedFilters`), because a filter set
 * minus one clause is a different board than the sender's.
 */
function isValidPropertyFilter(value: unknown): value is FilterExpression {
  if (!isPlainRecord(value)) return false;
  if (typeof value.property !== 'string') return false;
  // A non-`property` expression type (`children_count`, `elapsed_in_stage`, ...)
  // canonically carries `property: ''` — the condition is computed from the arc,
  // not read off a named property. Requiring a non-empty `property` rejected every
  // one of them; see `isUnconfiguredLanePayload` for why that asymmetry is a defect
  // and not hardening. `type` itself is validated against the canonical union so an
  // arbitrary string cannot buy an empty `property` past this check.
  if (value.type !== undefined && !(typeof value.type === 'string' && value.type in CANONICAL_EXPRESSION_TYPES)) {
    return false;
  }
  const isCalculated = typeof value.type === 'string' && value.type !== 'property';
  if (value.property.length === 0 && !isCalculated) return false;
  return typeof value.operator === 'string' && value.operator in CANONICAL_OPERATORS;
}

/**
 * The app itself persists an "unconfigured lane row" as an otherwise-empty payload,
 * deliberately: `filterStateToUnified` pushes `{}` for a lane row whose value is
 * malformed rather than dropping the row, to keep the condition present and fail
 * closed at the engine (never-widen — `contextFlowSerialization.ts:286-305`), and
 * `propertyRowToGroupLeaf` does the group-nested equivalent, carrying only the
 * editor's row `id`.
 *
 * Rejecting that shape made a board the app permits unshareable, and did so at the
 * WRONG END: `encodeFilterLink` validates nothing, so a URL was produced, copied and
 * counted as a successful share, and the RECIPIENT's whole link was then refused —
 * leaving them their own filters, a strictly WIDER board than the sender's
 * fail-closed one, which is the single thing every spec in this arc forbids. The
 * missing invariant is `decode(encode(x)) !== null` for every envelope the app
 * itself composes; `roundTripsEveryEnvelopeTheAppItselfComposes` now pins it.
 *
 * Accepting it cannot widen: the payload carries no operator and no targets, so it
 * fails closed for the recipient exactly as it does for the sender. `id` is the one
 * key allowed through because it is editor bookkeeping, never read by evaluation.
 */
function isUnconfiguredLanePayload(value: Record<string, unknown>): boolean {
  return Object.keys(value).every((key) => key === 'id' && typeof value.id === 'string');
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
}

/**
 * The lane validators below exist for the same reason `isValidPropertyFilter`
 * does, and they are NOT optional hardening. Two things dereference a decoded
 * clause before anything renders:
 *
 * - `hasVariableToken` (below) reads `assignees`/`relatedTo` with `.some(...)`,
 *   and `useSharedFilterLink` calls it synchronously inside its mount effect —
 *   so a `?f=` carrying `{"perspectiveFilters":[{}]}` would throw during mount
 *   and blank the page, on exactly the input the `malformedNotice` path exists
 *   to handle gracefully.
 * - the clauses flow into the execute request and the chip display, so a
 *   non-canonical `operator` in a lane other than `propertyFilters` reaches the
 *   backend by a route `CANONICAL_OPERATORS` alone does not cover.
 *
 * `in`/`not_in` are the only operators these three lanes define (`types/filters.d.ts`).
 */
function isLaneOperator(value: unknown): boolean {
  return value === 'in' || value === 'not_in';
}

function isValidPerspectiveFilter(value: unknown): value is PerspectiveFilter {
  if (!isPlainRecord(value)) return false;
  if (isUnconfiguredLanePayload(value)) return true;
  if (typeof value.perspectiveSystemName !== 'string' || value.perspectiveSystemName.length === 0) return false;
  if (!isLaneOperator(value.operator)) return false;
  return isStringArray(value.assignees);
}

function isValidRelationshipFilter(value: unknown): value is RelationshipFilter {
  if (!isPlainRecord(value)) return false;
  if (isUnconfiguredLanePayload(value)) return true;
  if (typeof value.relationshipSystemName !== 'string' || value.relationshipSystemName.length === 0) return false;
  if (!isLaneOperator(value.operator)) return false;
  return isStringArray(value.relatedTo);
}

/**
 * Both siblings above terminate in an array check on the field their consumers
 * dereference (`assignees`, `relatedTo`). This one used to stop at `operator`,
 * which left the three predicate axes completely unvalidated.
 *
 * That was latent only because no surface rendered this lane. EAINT-793 R-19
 * wires `arcFilters` into `FilterChipsDisplayMolecule` on BOTH the popover and
 * the filter bar, and the renderer it reaches does
 * `(filter.healthStatus ?? []).forEach(...)` (`renderFilterExpression.ts:194-196`)
 * — so a crafted `?f=` carrying `healthStatus: "boom"` decoded clean and then
 * threw `forEach is not a function` inside render, with no `try` and no error
 * boundary above it: a blanked board from a URL, which is the exact failure the
 * lane validators' docblock says they exist to prevent ("the clauses flow into
 * the execute request AND the chip display").
 *
 * `minCount` is checked too — it reaches `renderArcFilter`'s `> 1` comparison
 * and the server's clause builder, where a non-number is meaningless.
 */
function isValidArcFilter(value: unknown): value is ArcFilter {
  if (!isPlainRecord(value)) return false;
  if (isUnconfiguredLanePayload(value)) return true;
  if (!isLaneOperator(value.operator)) return false;
  if (value.arcSelector !== undefined && value.arcSelector !== 'any' && value.arcSelector !== 'primary') return false;
  if (value.templateSystemName !== undefined && typeof value.templateSystemName !== 'string') return false;
  if (value.minCount !== undefined && typeof value.minCount !== 'number') return false;
  return (['stageId', 'healthStatus', 'lifecycle'] as const).every(
    (axis) => value[axis] === undefined || isStringArray(value[axis])
  );
}

/** A group leaf is either a wrapped lane condition (`kind` discriminant) or a bare property expression. */
function isValidFilterGroupLeaf(value: unknown): boolean {
  if (!isPlainRecord(value)) return false;
  if (value.kind === 'perspective') return isValidPerspectiveFilter(value.filter);
  if (value.kind === 'relationship') return isValidRelationshipFilter(value.filter);
  if (value.kind === 'arc') return isValidArcFilter(value.filter);
  return isValidPropertyFilter(value);
}

/**
 * A group is validated as a WHOLE. It is one AND/OR unit; silently dropping one
 * leaf out of an `or` would widen it and out of an `and` would narrow it, either
 * way inventing a filter the sender never wrote. An invalid group rejects the
 * whole link, on the same rule that now governs the flat lanes — see
 * `sanitizeUnifiedFilters`.
 */
function isValidFilterGroup(value: unknown, depth = 0): value is FilterGroup {
  // Over the bound the group is REJECTED, not truncated — same rule as the rest
  // of this function: a group is one AND/OR unit, and lopping off its tail
  // would invent a filter the sender never wrote.
  if (depth > MAX_FILTER_GROUP_DEPTH) return false;
  if (!isPlainRecord(value)) return false;
  if (value.operator !== 'and' && value.operator !== 'or') return false;
  if (!Array.isArray(value.expressions) || !value.expressions.every(isValidFilterGroupLeaf)) return false;
  if (value.groups === undefined) return true;
  // Explicit arrow, NOT a bare `.every(isValidFilterGroup)`: `every`/`filter`
  // pass (element, INDEX, array), so a bare reference would bind the array
  // index to `depth` and defeat the bound entirely.
  return Array.isArray(value.groups) && value.groups.every((group) => isValidFilterGroup(group, depth + 1));
}

/**
 * `btoa`/`atob` operate on "binary strings" (one UTF-16 code unit per byte,
 * 0-255) — they are not Unicode-aware. These convert an arbitrary string to
 * and from that binary-string form via its UTF-8 bytes, without relying on
 * the WHATWG `TextEncoder`/`TextDecoder` globals, which aren't available in
 * every host this code runs under (including this repo's jsdom test
 * environment). `escape`/`unescape` are deprecated for URI escaping but are
 * still the standard trick for this exact byte-level round trip.
 *
 * Measured in this workspace's jest env, not assumed: `typeof TextEncoder` and
 * `typeof TextDecoder` are both `'undefined'` while `typeof btoa` is
 * `'function'`. Switching to TextEncoder/TextDecoder — proposed in review on the
 * belief that Node 18+ puts them on `globalThis` — would break every test in
 * this file. Re-verify before changing.
 */
function utf8ToBinaryString(value: string): string {
  return unescape(encodeURIComponent(value));
}

function binaryStringToUtf8(value: string): string {
  return decodeURIComponent(escape(value));
}

function toBase64Url(value: string): string {
  // Base64 padding is 0-2 '=' by definition, so a BOUNDED quantifier says
  // exactly what is possible and removes the unbounded `+` whose backtracking
  // Sonar flags as super-linear (S8786).
  return btoa(utf8ToBinaryString(value)).replaceAll('+', '-').replaceAll('/', '_').replace(/={1,2}$/, '');
}

function fromBase64Url(value: string): string | null {
  if (!/^[A-Za-z0-9_-]+$/.test(value)) return null;
  const normalized = value.replaceAll('-', '+').replaceAll('_', '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  try {
    return binaryStringToUtf8(atob(padded));
  } catch {
    return null;
  }
}

/**
 * Copies only the five canonical `UnifiedFilters` lanes, by literal key name.
 * Not a loop over `Object.keys(value)`.
 *
 * ALL-OR-NOTHING, by ratified rule: a lane that is present but holds any
 * element this build cannot validate rejects the WHOLE link (`null`), rather
 * than dropping the bad clause and returning the remainder. The spec and
 * `docs/overview/contexts-system.md` both state it — "never thrown, never
 * partially applied — half a filter set is a different board wearing the
 * sender's URL" — and the earlier drop-and-continue shape quietly broke it.
 *
 * The scenario that makes this more than pedantry is version skew, not attack:
 * a sender on a deploy that has added a `FilterOperator` shares a link; the
 * recipient's bundle is one deploy behind, so `CANONICAL_OPERATORS` lacks it.
 * Dropping that clause hands the recipient strictly MORE rows than the sender
 * saw, under a banner reading "Viewing a shared filter set", with no surface
 * able to say anything was lost — the same silent-divergence failure
 * `noLevelMatch` exists to prevent, reached by a path no notice can see.
 * Rejecting instead degrades into the already-built malformed path: `?f=`
 * cleared, one notice, and the recipient's own board.
 *
 * Returns `null` to reject, or the lanes plus a count of surviving clauses —
 * the caller needs the count to tell "a link that filters nothing" from "a
 * link that carries filters" (see `decodeFilterLink`).
 */
function sanitizeUnifiedFilters(value: unknown): { filters: UnifiedFilters; clauses: number } | null {
  if (!isPlainRecord(value)) return null;

  const result: UnifiedFilters = {};
  let clauses = 0;

  // Element-level validation, not a blind cast — see `isValidPropertyFilter`.
  if (value.propertyFilters !== undefined) {
    if (!Array.isArray(value.propertyFilters) || !value.propertyFilters.every(isValidPropertyFilter)) return null;
    result.propertyFilters = value.propertyFilters;
    clauses += value.propertyFilters.length;
  }
  if (value.perspectiveFilters !== undefined) {
    if (!Array.isArray(value.perspectiveFilters) || !value.perspectiveFilters.every(isValidPerspectiveFilter)) return null;
    result.perspectiveFilters = value.perspectiveFilters;
    clauses += value.perspectiveFilters.length;
  }
  if (value.relationshipFilters !== undefined) {
    if (!Array.isArray(value.relationshipFilters) || !value.relationshipFilters.every(isValidRelationshipFilter)) return null;
    result.relationshipFilters = value.relationshipFilters;
    clauses += value.relationshipFilters.length;
  }
  if (value.arcFilters !== undefined) {
    if (!Array.isArray(value.arcFilters) || !value.arcFilters.every(isValidArcFilter)) return null;
    result.arcFilters = value.arcFilters;
    clauses += value.arcFilters.length;
  }
  if (value.filterGroups !== undefined) {
    // Arrow rather than a bare reference — see the note in `isValidFilterGroup`.
    if (!Array.isArray(value.filterGroups) || !value.filterGroups.every((group) => isValidFilterGroup(group))) return null;
    result.filterGroups = value.filterGroups;
    clauses += value.filterGroups.length;
  }

  return { filters: result, clauses };
}

/** `?f=` — base64url of the JSON `SharedFilterLinkEnvelope`. Returns `null` when the encoded
 *  payload exceeds `SHARE_LINK_MAX_CHARS` rather than truncating it. */
export function encodeFilterLink(envelope: SharedFilterLinkEnvelope): string | null {
  const json = JSON.stringify(envelope);
  let encoded: string;
  try {
    encoded = toBase64Url(json);
  } catch {
    return null;
  }
  return encoded.length <= SHARE_LINK_MAX_CHARS ? encoded : null;
}

/**
 * The `?f=` character count an envelope WOULD occupy — the same unit
 * `encodeFilterLink(...)!.length` reports, and the unit `SHARE_LINK_MAX_CHARS`
 * is expressed in — including for envelopes that are over budget and so never
 * get a string built for them.
 *
 * Exists because the refusal path had nothing else to measure with and reached
 * for `JSON.stringify(envelope).length` (EAINT-793 R-15). base64 inflates ~4/3,
 * so that ran the refused series ~25% BELOW the budget's own unit: the two
 * series could not be pooled, which is exactly the job the spec assigns
 * `payloadBytes` — the evidence the 1500-char budget gets re-judged on.
 *
 * Computed from the UTF-8 byte length rather than by encoding, so measuring an
 * over-budget payload never materializes the oversized string. base64url drops
 * padding, hence `ceil(bytes * 4 / 3)` rather than `ceil(bytes / 3) * 4`.
 *
 * Returns `null` if the measurement itself cannot be made, mirroring
 * `encodeFilterLink`'s own `catch`: this helper is called from an analytics
 * path, so it must not throw anywhere encode would merely answer `null`. That
 * guard is a backstop, not a live path — `JSON.stringify` has escaped lone
 * surrogates since ES2019, so what reaches `utf8ToBinaryString` is already
 * well-formed and both functions succeed even on `'\uD800'`. Kept because the
 * two must not diverge if that ever stops being true, and a plausible-looking
 * number for a string that cannot exist is exactly the pollution this helper
 * was added to remove.
 */
export function filterLinkEncodedLength(envelope: SharedFilterLinkEnvelope): number | null {
  try {
    return Math.ceil((utf8ToBinaryString(JSON.stringify(envelope)).length * 4) / 3);
  } catch {
    return null;
  }
}

/** Decodes a `?f=` param. Returns `null` on anything malformed — never throws, never
 *  partially applies. See file header for the prototype-pollution guard this enforces. */
export function decodeFilterLink(param: string): SharedFilterLinkEnvelope | null {
  if (typeof param !== 'string' || param.length === 0) return null;
  // Symmetry with `encodeFilterLink`, which refuses to PRODUCE anything longer:
  // a param over the budget cannot have come from this app, so accepting it
  // only widens the surface that the sanitizers below have to survive. Cheapest
  // possible rejection, before any decode or parse work is done on it.
  if (param.length > SHARE_LINK_MAX_CHARS) return null;

  const json = fromBase64Url(param);
  if (json === null) return null;

  // The whole sanitize step is guarded, not just `JSON.parse`. The contract
  // above says "never throws", and `useSharedFilterLink` relies on it literally
  // — it calls this inside a mount effect with no `try`, so anything escaping
  // here blanks the page instead of raising `malformedNotice`. The recursive
  // validators are bounded (see `MAX_FILTER_GROUP_DEPTH`), so this is the
  // backstop for the next unbounded thing someone adds, not the primary fix.
  try {
    const parsed: unknown = JSON.parse(json);
    if (!isPlainRecord(parsed)) return null;

    // D-3: a link with no addressee cannot be matched against a shell's
    // active context, so it is malformed for this feature's purposes — same
    // treatment as a link with zero clauses (below). Checked first and
    // copied by literal key name, same as every other field this function
    // returns (never a dynamic bracket assignment).
    if (typeof parsed.contextSystemName !== 'string' || parsed.contextSystemName.length === 0) return null;

    const envelope: SharedFilterLinkEnvelope = { contextSystemName: parsed.contextSystemName };
    let clauses = 0;

    if (parsed.global !== undefined) {
      const global = sanitizeUnifiedFilters(parsed.global);
      if (global === null) return null;
      if (global.clauses > 0) envelope.global = global.filters;
      clauses += global.clauses;
    }

    if (parsed.byLevelId !== undefined) {
      if (!isPlainRecord(parsed.byLevelId)) return null;
      const source = parsed.byLevelId;
      const byLevelId: Record<string, UnifiedFilters> = {};
      for (const levelId of Object.keys(source)) {
        // Rejected whole, not skipped. A prototype key here cannot come from
        // `encodeFilterLink` — it is a crafted payload, and handing back the
        // "clean" remainder of one is both a partial application and a
        // needlessly generous answer to an attack.
        if (UNSAFE_KEYS.has(levelId)) return null;
        const laneFilters = sanitizeUnifiedFilters(source[levelId]);
        if (laneFilters === null) return null;
        if (laneFilters.clauses > 0) byLevelId[levelId] = laneFilters.filters;
        clauses += laneFilters.clauses;
      }
      if (Object.keys(byLevelId).length > 0) envelope.byLevelId = byLevelId;
    }

    // A well-formed envelope carrying no clause at all (`?f=e30`, or every lane
    // an empty array) is malformed for this feature's purposes, not merely
    // empty. Returning it would activate shared-link mode — which SUPPRESSES
    // the recipient's own view/user/session layers on every level — while
    // contributing nothing to filter with. The recipient's saved filters would
    // silently stop applying, the board would show everything, and the banner
    // would assert they are looking at someone else's filter set.
    if (clauses === 0) return null;

    return envelope;
  } catch {
    return null;
  }
}

/**
 * READER-dependent, not merely variable (EAINT-793 R-20).
 *
 * `isFilterVariable` matches all five context tokens, but only `@me` resolves
 * against the person reading: `assembleVariableContext` reads
 * `request.userProfile.identityId` for it, while `@root`/`@parent`/`@center`
 * resolve against the BOARD ROOT — the same root the link's own URL carries, so
 * they give the sender and the recipient identical rows — and `@today` resolves
 * against the clock, which is not a property of who is reading.
 *
 * This predicate exists because everything downstream of it makes a claim about
 * IDENTITY, not about variability: the banner says "shows your own items" and
 * "you're seeing your own items, not the sender's", and the spec's whole
 * rationale (§ "`@me` is preserved as a token") is about identity resolution.
 * Matching all five made those sentences false for a link whose only token was
 * `@root` — a warning about a divergence that cannot happen.
 *
 * `@today` is deliberately NOT included. A shared link is a live query, not a
 * snapshot; nothing in this feature promises time-invariant rows (the
 * underlying data moves too), so time drift is not the divergence the banner
 * is about. Warning about it in copy that says "your own items" would be a
 * second false statement rather than a fix for the first.
 */
function isReaderDependentVariable(value: unknown): boolean {
  return isFilterVariable(value) && value === '@me';
}

function expressionHasVariableToken(expression: FilterExpression): boolean {
  if (isReaderDependentVariable(expression.value)) return true;
  return Array.isArray(expression.values) && expression.values.some(isReaderDependentVariable);
}

/**
 * `Array.isArray` rather than a bare `.some` — matching `expressionHasVariableToken`
 * above, and NOT redundant with the lane validators. `isUnconfiguredLanePayload` now
 * (correctly) admits `{}`, which has no `assignees`/`relatedTo` at all, so a bare
 * `.some` would throw a TypeError here. That matters more than it looks: this runs
 * inside `useSharedFilterLink`'s mount effect, which has no `try`, so the throw would
 * blank the page instead of raising `malformedNotice` — the precise failure the
 * validators' own docblock says they exist to prevent. Totality has to live at both
 * ends: the validators decide what is admissible, these stay total over it.
 */
function perspectiveHasVariableToken(filter: PerspectiveFilter): boolean {
  return Array.isArray(filter.assignees) && filter.assignees.some(isReaderDependentVariable);
}

function relationshipHasVariableToken(filter: RelationshipFilter): boolean {
  return Array.isArray(filter.relatedTo) && filter.relatedTo.some(isReaderDependentVariable);
}

function groupHasVariableToken(group: FilterGroup): boolean {
  const leafHit = group.expressions?.some((leaf) => {
    if (isPropertyLeaf(leaf)) return expressionHasVariableToken(leaf);
    if (isPerspectiveLeaf(leaf)) return perspectiveHasVariableToken(leaf.filter);
    if (isRelationshipLeaf(leaf)) return relationshipHasVariableToken(leaf.filter);
    return false; // arc leaves carry no variable-able fields
  });
  if (leafHit) return true;
  return Boolean(group.groups?.some(groupHasVariableToken));
}

function unifiedFiltersHasVariableToken(filters: UnifiedFilters | undefined): boolean {
  if (!filters) return false;
  return Boolean(
    filters.propertyFilters?.some(expressionHasVariableToken) ||
      filters.perspectiveFilters?.some(perspectiveHasVariableToken) ||
      filters.relationshipFilters?.some(relationshipHasVariableToken) ||
      filters.filterGroups?.some(groupHasVariableToken)
  );
}

/** True when any lane, in `global` or any `byLevelId` entry, contains a token whose
 *  resolution depends on WHO is reading — `@me` today; see
 *  `isReaderDependentVariable` for why the other four context tokens do not count.
 *  Drives `SharedLinkBannerMolecule`'s identity-divergence variant and the
 *  `context_filter_link_opened`/`context_filter_link_shared` analytics metadata. */
export function hasVariableToken(envelope: RuntimeFilterEnvelope | undefined): boolean {
  if (!envelope) return false;
  if (unifiedFiltersHasVariableToken(envelope.global)) return true;
  return Object.values(envelope.byLevelId ?? {}).some(unifiedFiltersHasVariableToken);
}
